import PageHeader from '../components/ui/PageHeader';
import { userFacingError } from '../utils/userFacingError';
import { publishedArticleUrl } from '../utils/publishedArticleUrl';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { PublicationService } from '../services/publicationService';
import PublicationChecklist from '../components/PublicationChecklist';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { checkPublication } from '../utils/publicationCheck';
import { ArticleService } from '../services/articleService';
import { PlatformService } from '../services/platformService';
import { CategoryService } from '../services/categoryService';
import { DossierService } from '../services/dossierService';
import { AuthService } from '../services/authService';
import { ScheduleService } from '../services/scheduleService';
import { StorageService } from '../services/storageService';
import { formatContentAsHtml } from '../utils/htmlFormatter';
import { UrlValidator } from '../utils/urlValidator';
import { getArticleStatusText, getArticleDisplayStatus } from '../utils/articleStatus';
import { formatDateGerman, formatDateTimeGerman, formatScheduledAtGerman } from '../utils/dateFormatter';
import { parseScheduledAt } from '../utils/dateTimeUtils';
import { parseTags } from '../utils/tagUtils';
import StatusBadge from '../components/ui/StatusBadge';
import ArticleTypeBadge from '../components/ui/ArticleTypeBadge';
import { Article, Platform, Category, Dossier } from '../types';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import PageLoader from '../components/ui/PageLoader';
import { RescheduleModal } from '../components/RescheduleModal';
import { ScheduleConfirmModal } from '../components/ScheduleConfirmModal';
import { 
  ArrowLeftIcon,
  CalendarIcon,
  GlobeAltIcon,
  FolderIcon,
  PhotoIcon,
  DocumentTextIcon,
  CloudArrowUpIcon,
  TrashIcon,
  MusicalNoteIcon,
  ClockIcon,
  PencilIcon,
  LinkIcon
} from '@heroicons/react/24/outline';

export const ArticleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState<Article | null>(null);
  const publicUrl = article && (article.status === 'published' || article.publicationState === 'accepted')
    ? publishedArticleUrl(article.publishedUrl) || publishedArticleUrl(article.publicationCandidateUrl) : undefined;
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPreflight, setShowPreflight] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Reschedule Modal State
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showScheduleConfirmModal, setShowScheduleConfirmModal] = useState(false);

  useEffect(() => {
    if (!user || !id) return;
    return ArticleService.subscribeToUpdates(user, updated => {
      if (updated.id === id) setArticle(updated);
    });
  }, [user, id]);

  useEffect(() => {
    const loadArticleData = async () => {
      if (!id || !user) return;

      setIsLoading(true);
      try {
        // Artikel laden
        const articleData = await ArticleService.getArticle(id, user);
        setArticle(articleData);

        // Zugehörige Daten laden
        const [platformData, categoryData, dossierData] = await Promise.all([
          PlatformService.getPlatformById(articleData.platformId),
          articleData.categoryId ? CategoryService.getCategoryById(articleData.categoryId) : null,
          articleData.dossierId ? DossierService.getDossierById(articleData.dossierId) : null
        ]);

        setPlatform(platformData);
        setCategory(categoryData);
        setDossier(dossierData);
      } catch (error) {
        toast.error('Fehler beim Laden der Artikel-Daten');
      } finally {
        setIsLoading(false);
      }
    };

    loadArticleData();
  }, [id, user]);

  // Status-Funktionen werden jetzt aus der zentralen Utility importiert

  // Reschedule Modal Handler
  const openRescheduleModal = () => {
    setShowRescheduleModal(true);
  };

  const closeRescheduleModal = () => {
    setShowRescheduleModal(false);
  };

  const handleRescheduleSuccess = async () => {
    // Artikel neu laden nach erfolgreichem Umplanen
    if (!id || !user) return;
    try {
      const updatedArticle = await ArticleService.getArticle(id, user);
      setArticle(updatedArticle);
    } catch (error) {
      console.error('Fehler beim Neu-Laden des Artikels:', error);
    }
  };

  const openScheduleConfirm = () => {
    if (!article || !user) return;
    setShowPreflight(true);
    const checks = checkPublication(article, platform, category ? [category] : [], dossier ? [dossier] : [], (article.sources || []).map(s => ({ ...s, isValid: true, isProcessing: false })));
    if (checks.every(check => check.ok)) setShowScheduleConfirmModal(true);
    else toast.error('Bitte vervollständige die offenen Punkte der Einreichungsprüfung.');
  };

  // Artikel einreichen
  const submitArticle = async (confirmedSlot?: Date) => {
    if (!article || !user || !platform || isSubmitting) return;
    const checks = checkPublication(article, platform, category ? [category] : [], dossier ? [dossier] : [], (article.sources || []).map(s => ({ ...s, isValid: true, isProcessing: false })));
    if (!checks.every(check => check.ok) || !confirmedSlot || confirmedSlot.getTime() <= Date.now()) { toast.error('Bitte Angaben und einen zukünftigen Veröffentlichungstermin bestätigen.'); return; }
    
    setIsSubmitting(true);
    try {
      console.log('🚀 Starte echte API-Einreichung für bestehenden Artikel...');
      
      // Tags als Array konvertieren (unterstützt Array und String)
      const tagsArray = parseTags(article.tags);

      const resolvedCategory = category;
      const resolvedDossier = dossier;
      const resolvedCategoryId = article.categoryId;
      const resolvedDossierId = article.dossierId;

      if (!resolvedCategory || !resolvedDossier) {
        throw new Error('Für die gewählte Plattform ist keine Kategorie oder kein Dossier vorhanden.');
      }
      
      // Autorname ermitteln: Wenn eingeloggter User = Artikel-Autor, dann eigenen Namen verwenden,
      // sonst den echten Autor über getUserById laden (für Admin/Redakteur die fremde Artikel einreichen)
      let authorName: string;
      let authorId: string;
      if (user.id === article.authorId) {
        // Autor reicht seinen eigenen Artikel ein
        authorName = user.name;
        authorId = user.id;
      } else {
        // Admin/Redakteur reicht fremden Artikel ein - echten Autor laden
        const author = await AuthService.getUserById(article.authorId);
        authorName = author.name;
        authorId = author.id;
      }
      
      // ImageType ermitteln (Fallback für ältere Entwürfe)
      let effectiveImageType: 'eigenes' | 'ki' | 'redaktion' = article.imageType || 'eigenes';
      if (!article.imageType) {
        if (article.imageSourceUrl?.includes('openai.com')) {
          effectiveImageType = 'ki';
        } else if (!article.imageStorageUrl || article.imageStorageUrl.includes('68e4ee76002eac11ff3e')) {
          effectiveImageType = 'redaktion';
        }
      }

      // Validierung für Bilddaten wie im Wizard
      if (effectiveImageType === 'ki' && !article.imagePrompt?.trim()) {
        toast.error('Bitte gib einen Bild-Prompt für die KI-Generierung ein');
        setIsSubmitting(false);
        return;
      }
      if (effectiveImageType === 'eigenes') {
        if (!article.imageSource?.trim()) {
          toast.error('Bitte gib eine Bildquelle an');
          setIsSubmitting(false);
          return;
        }
        if (!article.imageStorageUrl?.trim()) {
          toast.error('Bitte lade ein Bild hoch');
          setIsSubmitting(false);
          return;
        }
        if (!article.imageCaption?.trim()) {
          toast.error('Bitte gib eine Bildunterschrift an');
          setIsSubmitting(false);
          return;
        }
        if (!article.imageSourceUrl?.trim()) {
          toast.error('Bitte gib eine Bildquelle-URL an');
          setIsSubmitting(false);
          return;
        }
        const validation = UrlValidator.validateImageSourceUrl(article.imageSourceUrl);
        if (!validation.isValid) {
          toast.error(validation.error || 'Bitte gib eine gültige URL für die Bildquelle ein');
          setIsSubmitting(false);
          return;
        }
      }

      // Schedule des Autors laden, um die ORIGINAL-Zeit aus der DB zu bekommen
      const authorSchedules = await ScheduleService.getSchedulesByAuthor(authorId);
      const platformSchedule = authorSchedules.find(
        schedule => schedule.platformId === article.platformId && schedule.isActive
      );
      
      // Veröffentlichungstermin neu berechnen (wie im Wizard)
      const scheduledAtDate = confirmedSlot
        ? confirmedSlot
        : platformSchedule
        ? await ScheduleService.findNextAvailableSlotForAuthor(authorId, article.platformId, platformSchedule, user)
        : (article.scheduledAt ? parseScheduledAt(article.scheduledAt) : null);
      
      // Quellen temporär zum Content hinzufügen (NUR für API-Payload, NICHT in DB speichern)
      let contentForApi = article.content;
      if (article.sources && article.sources.length > 0) {
        let sourcesText = '\n\n**Quellen:**\n\n';
        article.sources.forEach(source => {
          sourcesText += `<a href="${source.url}" target="_blank">${source.title}</a>\n\n`;
        });
        contentForApi = article.content + sourcesText;
      }
      
      // API-Payload erstellen (mit Quellen im Content)
      const apiPayload = {
        title: article.title,
        content: formatContentAsHtml(contentForApi), // Content mit temporär angehängten Quellen
        subtitle: article.subtitle,
        teaser: article.teaser,
        topic: article.topic,
        tags: tagsArray,
        seoTitle: article.metaTitle || article.title,
        // WICHTIG: Verwende das Datum aus scheduledAt, aber die ZEIT aus dem Schedule (DB)
        // So wird die korrekte deutsche Zeit (z.B. 10:00) an die API gesendet
        publishDate: scheduledAtDate ? (() => {
          const dateObj = scheduledAtDate;
          // Konvertiere zu deutscher Zeit für das Datum
          return formatDateGerman(dateObj);
        })() : new Date().toISOString().split('T')[0],
        publishTime: platformSchedule?.time || '10:00:00',
        platform: {
          name: platform.name
        },
        category: {
          name: resolvedCategory.name
        },
        dossier: {
          title: resolvedDossier.title
        },
        image: {
          prompt: article.imagePrompt,
          url: effectiveImageType === 'redaktion' ? 'https://appwrite.e-redaktion.org/v1/storage/buckets/article-images/files/68e4ee76002eac11ff3e/view?project=689c6acc001bcf54fcdc' : article.imageStorageUrl, // Redaktions-Bild
          source: effectiveImageType === 'redaktion' ? 'Redaktion' : article.imageSource, // Dummy-Source für Redaktion
          sourceUrl: effectiveImageType === 'ki' ? 'https://www.openai.com' : (effectiveImageType === 'redaktion' ? 'https://www.google.de' : article.imageSourceUrl), // Dummy-URL für Redaktion
          caption: effectiveImageType === 'redaktion' ? (article.imageCaption || 'Bild von der Redaktion ausgewählt') : article.imageCaption,
          description: effectiveImageType === 'redaktion' ? (article.imageCaption || 'Bild von der Redaktion ausgewählt') : article.imageCaption,
          type: effectiveImageType
        },
        author: {
          name: authorName
        }
      };
      
      console.log('📋 API-Payload:', apiPayload);
      
      // Status nur bei Entwurf auf "ready" setzen; bei Re-Submit bleibt er unverändert auf "ready"
      const statusUpdate = article.status === 'draft' ? { status: 'ready' as const } : {};

      const updatedArticle = await PublicationService.enqueue(article, apiPayload, user, {
        ...statusUpdate,
        imageType: effectiveImageType,
        categoryId: resolvedCategoryId,
        dossierId: resolvedDossierId,
        scheduledAt: scheduledAtDate ? scheduledAtDate.toISOString() : article.scheduledAt
      });

      setArticle(updatedArticle);
      setCategory(resolvedCategory);
      setDossier(resolvedDossier);
      setShowPreflight(false);
      toast.success('Artikel gespeichert und zur Veröffentlichung beauftragt. Du kannst den Browser jetzt schließen.', { duration: 6000 });
    } catch (error) {
      console.error('Submit article error:', error);
      toast.error(userFacingError(error, 'Der Veröffentlichungsauftrag konnte nicht gespeichert werden.'), { duration: 10000 });
      try { setArticle(await ArticleService.getArticle(article.id, user)); } catch { /* Der bestehende Artikel bleibt sichtbar. */ }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Artikel löschen
  const deleteArticle = async () => {
    if (!article || !user) return;
    
    setIsDeleting(true);
    try {
      await ArticleService.deleteArticle(article.id, user);
      toast.success('Artikel erfolgreich gelöscht!');
      navigate('/articles'); // Zurück zur Artikel-Liste
    } catch (error) {
      toast.error('Fehler beim Löschen des Artikels');
    } finally {
      setShowDeleteConfirm(false);
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (!article) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">Artikel konnte nicht geladen werden.</p>
        </div>
      </div>
    );
  }

  const tags = parseTags(article.tags);
  const isEditorOrAdmin = Boolean(
    user?.roles.includes('administrator') || user?.roles.includes('redakteur')
  );
  const isResubmission = article.status === 'ready';
  const deliveryBlocked = ['queued', 'sending', 'accepted', 'uncertain'].includes(article.publicationState || '');
  const canSubmitArticle = !deliveryBlocked && (article.status === 'draft' || (isResubmission && (isEditorOrAdmin || (article.publicationState === 'failed' && article.authorId === user?.id))));
  const processing = ['queued', 'sending'].includes(article.publicationState || '');
  const canEditArticle = !processing && (
    (['draft', 'rework_requested'].includes(article.status) && (user?.id === article.authorId || isEditorOrAdmin)) ||
    (isResubmission && (isEditorOrAdmin || (article.publicationState === 'failed' && article.authorId === user?.id))));
  const canRescheduleArticle = !processing && (article.status === 'draft' || isEditorOrAdmin);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <PageHeader title="Artikel" description="Prüfe Inhalt, Angaben und Veröffentlichungsstatus." />
        </div>
        <div className="flex flex-wrap items-center gap-3">
           
           {/* Einreichen-Button für Entwürfe sowie Re-Submit bei Redakteur/Admin */}
           {canSubmitArticle && (
             <Button
               onClick={openScheduleConfirm}
               disabled={isSubmitting}
               className="flex items-center"
             >
               {isSubmitting ? (
                 <>
                   <LoadingSpinner size="sm" />
                   <span className="ml-2">{isResubmission ? 'Wird erneut eingereicht...' : 'Wird eingereicht...'}</span>
                 </>
               ) : (
                 <>
                   <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                   {isResubmission ? 'Erneut einreichen' : 'Artikel einreichen'}
                 </>
               )}
             </Button>
           )}
           
           {/* Bearbeiten-Button für Entwürfe sowie eingereichte Artikel durch Redaktion/Admin */}
           {canEditArticle && (
             <Button
               variant="outline"
               onClick={() => navigate(`/articles/${article.id}/edit`)}
               className="flex items-center"
             >
               <PencilIcon className="w-4 h-4 mr-2" />
               Bearbeiten
             </Button>
           )}
           
           {/* Löschen-Button nur für Entwürfe */}
           {article.status === 'draft' && (
             <Button
               variant="outline"
               onClick={() => setShowDeleteConfirm(true)}
               disabled={isDeleting}
               className="flex items-center text-red-600 border-red-300 hover:bg-red-50"
             >
               {isDeleting ? (
                 <>
                   <LoadingSpinner size="sm" />
                   <span className="ml-2">Wird gelöscht...</span>
                 </>
                               ) : (
                  <>
                    <TrashIcon className="w-4 h-4 mr-2" />
                    Entwurf löschen
                  </>
                )}
             </Button>
           )}
         </div>
      </div>

      {article.publicationError && article.status !== 'published' && <div role="alert" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{userFacingError(article.publicationError, 'Die Übergabe konnte nicht bestätigt werden. Bitte kontaktiere die Redaktion.')}</div>}
      {article.publicationState === 'queued' && article.status !== 'published' && <div role="status" className="mb-6 rounded-lg bg-blue-50 p-4 text-sm text-blue-900">
        <p className="font-medium">Artikel gespeichert und zur Veröffentlichung beauftragt.</p>
        <p className="mt-1">Die Verarbeitung erfolgt automatisch im Hintergrund. Du kannst diese Seite oder den Browser jetzt schließen.</p>
      </div>}
      {article.publicationState === 'accepted' && article.status !== 'published' && <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <p className="font-medium">Die Übergabe wurde bestätigt.</p>
        <p className="mt-1">Die öffentliche Veröffentlichung wird automatisch geprüft.</p>
        {publicUrl && <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center font-medium text-blue-800 underline">
          Eingereichten Artikel auf der Plattform öffnen ↗
        </a>}
      </div>}
      {article.status === 'published' && publicUrl && <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
        <p className="font-medium">Die öffentliche Veröffentlichung wurde bestätigt.</p>
        <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center font-medium text-green-800 underline">
          Veröffentlichten Artikel auf der Plattform öffnen ↗
        </a>
        {article.publicationCheckedAt && <p className="mt-2 text-xs">Bestätigt am {formatDateTimeGerman(new Date(article.publicationCheckedAt))}</p>}
      </div>}
      {article.publicationState === 'sending' && article.status !== 'published' && <div role="status" className="mb-6 rounded-lg bg-blue-50 p-4 text-sm text-blue-900">Die Übergabe wird im Hintergrund verarbeitet. Du kannst diese Seite oder den Browser schließen. Der Status wird automatisch aktualisiert.</div>}
      {showPreflight && <div className="mb-6"><PublicationChecklist
        checks={checkPublication(article, platform, category ? [category] : [], dossier ? [dossier] : [], (article.sources || []).map(s => ({ ...s, isValid: true, isProcessing: false })))}
        scheduledAt={article.scheduledAt ? parseScheduledAt(article.scheduledAt) || undefined : undefined}
        onFix={() => navigate(`/articles/${article.id}/edit`)} /></div>}
      <ConfirmDialog open={showDeleteConfirm} title="Entwurf löschen" description={`Soll der Entwurf „${article.title}“ endgültig gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Endgültig löschen" onClose={() => setShowDeleteConfirm(false)} onConfirm={deleteArticle} busy={isDeleting} danger />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hauptinhalt */}
        <div className="lg:col-span-2 space-y-6">
          {/* Artikel-Informationen */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              Artikel-Informationen
            </h2>
            
                         <div className="space-y-4">
               <div>
                 <label className="text-sm font-medium text-gray-500">Thema</label>
                 <p className="text-gray-900">{article.topic}</p>
               </div>
               
               <div>
                 <label className="text-sm font-medium text-gray-500">Titel</label>
                 <p className="text-gray-900 font-medium">{article.title}</p>
               </div>
               
               <div>
                 <label className="text-sm font-medium text-gray-500">Untertitel</label>
                 <p className="text-gray-900">{article.subtitle}</p>
               </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Artikel-Text</label>
                <p className="text-gray-900 whitespace-pre-wrap">
                  {article.content}
                </p>
              </div>
            </div>
          </div>

          {/* Audio-Artikel Informationen */}
          {article.type === 'audio' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <MusicalNoteIcon className="w-5 h-5 mr-2" />
                Audio-Artikel Informationen
              </h2>
              
              <div className="space-y-4">
                {article.originalArticleUrl && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Original-Artikel URL</label>
                    <p className="text-gray-900">
                      <a 
                        href={article.originalArticleUrl}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        {article.originalArticleUrl}
                      </a>
                    </p>
                  </div>
                )}
                
                {article.originalArticleTitle && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Original-Artikel Titel</label>
                    <p className="text-gray-900">{article.originalArticleTitle}</p>
                  </div>
                )}
                
                {article.audioStorageUrl && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Audio-Datei</label>
                    <div className="mt-2 space-y-2">
                      <audio controls src={article.audioStorageUrl} className="w-full" />
                      <a
                        href={article.audioStorageUrl}
                        download
                        className="text-sm text-blue-600 hover:underline inline-flex items-center"
                      >
                        Audio herunterladen
                      </a>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Audio-Artikel Status</label>
                  <p className="text-gray-900">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      article.status === 'ready' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {getArticleStatusText(article)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Quellen */}
          {article.sources && article.sources.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <LinkIcon className="w-5 h-5 mr-2" />
                Quellen
              </h2>
              
              <div className="space-y-3">
                {article.sources.map((source, index) => (
                  <div key={source.id || index} className="flex items-start">
                    <LinkIcon className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium break-words"
                      >
                        {source.title}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bild-Informationen */}
          {(article.imageStorageUrl || article.imagePrompt || article.imageSource || article.imageCaption) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <PhotoIcon className="w-5 h-5 mr-2" />
                Bild-Informationen
              </h2>
              
              <div className="space-y-4">
                {article.imageStorageUrl && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bild</label>
                    <img
                      src={StorageService.getImagePreviewUrlFromStorageUrl(article.imageStorageUrl, 1280, 720)}
                      alt="Artikel-Bild"
                      className="mt-2 w-full aspect-video object-cover rounded-lg border"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.dataset.fallback !== 'true') {
                          img.dataset.fallback = 'true';
                          if (article.imageStorageUrl) {
                            img.src = article.imageStorageUrl;
                          }
                        }
                      }}
                    />
                  </div>
                )}
                
                {article.imageCaption && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bildunterschrift</label>
                    <p className="text-gray-900">{article.imageCaption}</p>
                  </div>
                )}
                
                {article.imageSource && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bildquelle</label>
                    <p className="text-gray-900">{article.imageSource}</p>
                  </div>
                )}
                
                {article.imageSourceUrl && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bild-URL</label>
                    <p className="text-gray-900 break-all">{article.imageSourceUrl}</p>
                  </div>
                )}
                
                {article.imagePrompt && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bild-Prompt für KI-Generierung</label>
                    <p className="text-gray-900">{article.imagePrompt}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadaten */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Metadaten</h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Teaser für Social-Media</label>
                <p className="text-gray-900">{article.teaser}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Meta-Titel</label>
                  <p className="text-gray-900">{article.metaTitle || 'Nicht gesetzt'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Meta-Beschreibung</label>
                  <p className="text-gray-900">{article.metaDescription || 'Nicht gesetzt'}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Tags</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {tags.length > 0 ? (
                    tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                        {tag}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500">Keine Tags</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Artikel-Typ und Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Status</h2>
            
            <div className="space-y-3">
              {/* Artikel-Typ Badge */}
              <div className="flex items-center">
                <ArticleTypeBadge type={article.type} size="lg" />
              </div>
              
              {/* Status Badge */}
              <div className="flex items-center">
                <StatusBadge status={getArticleDisplayStatus(article)} label={getArticleStatusText(article)} size="lg" />
              </div>
            </div>
          </div>

          {/* Organisations-Informationen */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Organisation</h2>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <GlobeAltIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <label className="text-sm font-medium text-gray-500">Plattform</label>
                  <p className="text-gray-900 font-medium">{platform?.name || 'Unbekannt'}</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <FolderIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <label className="text-sm font-medium text-gray-500">Kategorie</label>
                  <p className="text-gray-900 font-medium">{category?.name || 'Nicht zugewiesen'}</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <FolderIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <label className="text-sm font-medium text-gray-500">Dossier</label>
                  <p className="text-gray-900 font-medium">{dossier?.title || 'Nicht zugewiesen'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Zeitstempel */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Zeitstempel</h2>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <label className="text-sm font-medium text-gray-500">Erstellt am</label>
                  <p className="text-gray-900 font-medium">
                    {formatDateTimeGerman(article.createdAt)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center">
                <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <label className="text-sm font-medium text-gray-500">Aktualisiert am</label>
                  <p className="text-gray-900 font-medium">
                    {formatDateTimeGerman(article.updatedAt)}
                  </p>
                </div>
              </div>
              

              
              {article.status === 'published' && article.publishedAt && (
                <div className="flex items-center">
                  <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <label className="text-sm font-medium text-gray-500">Veröffentlicht am</label>
                    <p className="text-gray-900 font-medium">
                      {formatDateTimeGerman(article.publishedAt)}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Veröffentlichungstermin (aus scheduledAt) */}
              {article.scheduledAt && (
                <div className="flex items-center">
                  <ClockIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <label className="text-sm font-medium text-gray-500">Veröffentlichungstermin</label>
                    {canRescheduleArticle ? (
                      <button
                        onClick={openRescheduleModal}
                        className="text-blue-600 font-medium hover:text-blue-700 hover:underline cursor-pointer transition-colors text-left flex items-center gap-2"
                      >
                        <span>{formatScheduledAtGerman(article.scheduledAt)}</span>
                        <CalendarIcon className="w-4 h-4" />
                      </button>
                    ) : (
                      <p className="text-gray-900 font-medium">
                        {formatScheduledAtGerman(article.scheduledAt)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Revisionen */}
          {article.revisions && article.revisions.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Revisionen</h2>
              
              <div className="space-y-3">
                {article.revisions.map((revision, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        Revision {index + 1}
                      </span>
                                           <span className="text-xs text-gray-500">
                       {formatDateGerman(revision.createdAt)}
                     </span>
                   </div>
                   <p className="text-sm text-gray-600">
                     Änderungen an Titel, Inhalt oder Metadaten
                   </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reschedule Modal */}
      {showRescheduleModal && article && user && (
        <RescheduleModal
          article={article}
          currentUser={user}
          onClose={closeRescheduleModal}
          onSuccess={handleRescheduleSuccess}
        />
      )}
      {showScheduleConfirmModal && article && user && (
        <ScheduleConfirmModal
          authorId={article.authorId}
          platformId={article.platformId}
          currentUser={user}
          initialScheduledAt={article.scheduledAt || null}
          onClose={() => setShowScheduleConfirmModal(false)}
          onConfirm={(date) => {
            setShowScheduleConfirmModal(false);
            submitArticle(date);
          }}
          title="Veröffentlichungstermin vor Einreichung"
          confirmLabel="Termin verwenden & einreichen"
        />
      )}
    </div>
  );
};
